import Constants from 'expo-constants';

/**
 * Environment configuration for VerifyGate Mobile App
 * Integrates the comprehensive environment.js configuration
 */

interface ConfigType {
    API_BASE_URL: string;
    WS_BASE_URL: string;
    WEB_BASE_URL: string;
    SSL_PINS: string[];
    SENTRY_DSN: string;
    GOOGLE_CLIENT_ID: string;
    APPLE_CLIENT_ID: string;
    ENABLE_ANALYTICS: boolean;
    ENABLE_CRASH_REPORTING: boolean;
    LOG_LEVEL: string;
    CACHE_DURATION: number;
    REQUEST_TIMEOUT: number;
    MAX_RETRY_ATTEMPTS: number;
    ENABLE_OFFLINE_MODE: boolean;
    ENABLE_PUSH_NOTIFICATIONS: boolean;
    ENABLE_BIOMETRIC_AUTH: boolean;
    ENABLE_DARK_MODE: boolean;
    ENABLE_DEBUG_MENU: boolean;
    FEATURE_FLAGS: {
        SMART_SCAN: boolean;
        FACE_MATCHING: boolean;
        AUTO_CROP: boolean;
        VOICE_GUIDANCE: boolean;
        OFFLINE_SUPPORT: boolean;
        BIOMETRIC_AUTH: boolean;
        [key: string]: boolean;
    };
    [key: string]: unknown;
}

class EnvironmentConfig {
    private environment: string;
    private config!: ConfigType;

    constructor() {
        const expoEnvironment = Constants.expoConfig?.extra?.environment;
        const runtimeEnvironment = process.env.EXPO_PUBLIC_ENVIRONMENT;

        let resolvedEnvironment = runtimeEnvironment || expoEnvironment;

        if (!resolvedEnvironment && typeof process !== 'undefined' && process.env.NODE_ENV === 'production') {
            resolvedEnvironment = 'production';
        }

        if (!resolvedEnvironment) {
            const globalRef = typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;
            const hostname = globalRef?.location?.hostname;
            if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
                resolvedEnvironment = 'production';
            }
        }

        this.environment = resolvedEnvironment || 'development';
        this.loadConfig();
    }

    /**
     * Load configuration based on environment
     */
    private loadConfig() {
        switch (this.environment) {
            case 'production':
                this.config = {
                    // Domain aldıktan sonra bu URL'leri kullan:
                    // API_BASE_URL: 'https://api.verifygate.com',
                    // WS_BASE_URL: 'wss://api.verifygate.com',
                    // WEB_BASE_URL: 'https://verifygate.com',
                    
                    // Şu an Render default URL'leri:
                    API_BASE_URL: this.ensureApiBaseUrl(process.env.EXPO_PUBLIC_API_URL, 'https://verifygate-backend.onrender.com/api'),
                    WS_BASE_URL: process.env.EXPO_PUBLIC_WS_URL || 'wss://verifygate-backend.onrender.com',
                    WEB_BASE_URL: process.env.EXPO_PUBLIC_WEB_URL || 'https://verifygate-frontend.onrender.com',
                    SSL_PINS: process.env.EXPO_PUBLIC_SSL_PINS ? process.env.EXPO_PUBLIC_SSL_PINS.split(',').map((pin: string) => pin.trim()) : [],
                    SENTRY_DSN: process.env.EXPO_PUBLIC_SENTRY_DSN || 'https://f46f271fb883a5c3d6f4008888df4ee1@o4510177112686592.ingest.de.sentry.io/4510177121402960',
                    GOOGLE_CLIENT_ID: process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID || 'your-production-google-client-id',
                    APPLE_CLIENT_ID: process.env.EXPO_PUBLIC_APPLE_CLIENT_ID || 'your-production-apple-client-id',
                    ENABLE_ANALYTICS: true,
                    ENABLE_CRASH_REPORTING: true,
                    LOG_LEVEL: 'error',
                    CACHE_DURATION: 3600000, // 1 hour
                    REQUEST_TIMEOUT: 30000, // 30 seconds
                    MAX_RETRY_ATTEMPTS: 3,
                    ENABLE_OFFLINE_MODE: true,
                    ENABLE_PUSH_NOTIFICATIONS: true,
                    ENABLE_BIOMETRIC_AUTH: true,
                    ENABLE_DARK_MODE: true,
                    ENABLE_DEBUG_MENU: false,
                    FEATURE_FLAGS: {
                        SMART_SCAN: true,
                        FACE_MATCHING: true,
                        AUTO_CROP: true,
                        VOICE_GUIDANCE: true,
                        OFFLINE_SUPPORT: true,
                        BIOMETRIC_AUTH: true
                    }
                };
                break;
                
            case 'staging':
                this.config = {
                    API_BASE_URL: this.ensureApiBaseUrl(process.env.EXPO_PUBLIC_API_URL, 'https://verifygate-backend-staging.onrender.com/api'),
                    WS_BASE_URL: process.env.EXPO_PUBLIC_WS_URL || 'wss://verifygate-backend-staging.onrender.com',
                    WEB_BASE_URL: process.env.EXPO_PUBLIC_WEB_URL || 'https://verifygate-frontend-staging.onrender.com',
                    SSL_PINS: process.env.EXPO_PUBLIC_SSL_PINS ? process.env.EXPO_PUBLIC_SSL_PINS.split(',').map((pin: string) => pin.trim()) : [],
                    SENTRY_DSN: process.env.EXPO_PUBLIC_SENTRY_DSN || 'https://f46f271fb883a5c3d6f4008888df4ee1@o4510177112686592.ingest.de.sentry.io/4510177121402961',
                    GOOGLE_CLIENT_ID: process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID || 'your-staging-google-client-id',
                    APPLE_CLIENT_ID: process.env.EXPO_PUBLIC_APPLE_CLIENT_ID || 'your-staging-apple-client-id',
                    ENABLE_ANALYTICS: true,
                    ENABLE_CRASH_REPORTING: true,
                    LOG_LEVEL: 'warn',
                    CACHE_DURATION: 1800000, // 30 minutes
                    REQUEST_TIMEOUT: 25000, // 25 seconds
                    MAX_RETRY_ATTEMPTS: 3,
                    ENABLE_OFFLINE_MODE: true,
                    ENABLE_PUSH_NOTIFICATIONS: true,
                    ENABLE_BIOMETRIC_AUTH: true,
                    ENABLE_DARK_MODE: true,
                    ENABLE_DEBUG_MENU: true,
                    FEATURE_FLAGS: {
                        SMART_SCAN: true,
                        FACE_MATCHING: true,
                        AUTO_CROP: true,
                        VOICE_GUIDANCE: false,
                        OFFLINE_SUPPORT: true,
                        BIOMETRIC_AUTH: true
                    }
                };
                break;
                
            case 'development':
            default:
                this.config = {
                    API_BASE_URL: process.env.EXPO_PUBLIC_API_URL || 'http://localhost:5001',
                    WS_BASE_URL: process.env.EXPO_PUBLIC_WS_URL || 'ws://localhost:5001',
                    WEB_BASE_URL: process.env.EXPO_PUBLIC_WEB_URL || 'http://localhost:5173',
                    SSL_PINS: [], // No SSL pinning in development
                    SENTRY_DSN: process.env.EXPO_PUBLIC_SENTRY_DSN || 'https://f46f271fb883a5c3d6f4008888df4ee1@o4510177112686592.ingest.de.sentry.io/4510177121402962',
                    GOOGLE_CLIENT_ID: process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID || 'your-dev-google-client-id',
                    APPLE_CLIENT_ID: process.env.EXPO_PUBLIC_APPLE_CLIENT_ID || 'your-dev-apple-client-id',
                    ENABLE_ANALYTICS: false,
                    ENABLE_CRASH_REPORTING: false,
                    LOG_LEVEL: 'debug',
                    CACHE_DURATION: 300000, // 5 minutes
                    REQUEST_TIMEOUT: 15000, // 15 seconds
                    MAX_RETRY_ATTEMPTS: 2,
                    ENABLE_OFFLINE_MODE: true,
                    ENABLE_PUSH_NOTIFICATIONS: true,
                    ENABLE_BIOMETRIC_AUTH: true,
                    ENABLE_DARK_MODE: true,
                    ENABLE_DEBUG_MENU: true,
                    FEATURE_FLAGS: {
                        SMART_SCAN: true,
                        FACE_MATCHING: true,
                        AUTO_CROP: true,
                        VOICE_GUIDANCE: true,
                        OFFLINE_SUPPORT: true,
                        BIOMETRIC_AUTH: true
                    }
                };
                break;
        }
    }

    /**
     * Get configuration value by key
     */
    get<T = unknown>(key: string, defaultValue?: T): T {
        return (this.config[key] as T) !== undefined ? (this.config[key] as T) : (defaultValue as T);
    }

    /**
     * Ensure API base URL always points to /api root in production-like environments
     */
    private ensureApiBaseUrl(envValue: string | undefined, fallback: string): string {
        const source = envValue && envValue.trim().length > 0 ? envValue : fallback;
        const normalized = source.replace(/\/+$/, '');
        if (normalized.endsWith('/api')) {
            return normalized;
        }
        return `${normalized}/api`;
    }

    /**
     * Get all configuration
     */
    getAll(): ConfigType {
        return { ...this.config };
    }

    /**
     * Get current environment
     */
    getEnvironment(): string {
        return this.environment;
    }

    /**
     * Check if feature flag is enabled
     */
    isFeatureEnabled(feature: string): boolean {
        return this.config.FEATURE_FLAGS?.[feature] || false;
    }
}

// Create singleton instance
const environmentConfig = new EnvironmentConfig();

// Export the config object for backward compatibility
export default environmentConfig.getAll();

// Also export the instance for advanced usage
export { environmentConfig };

// Export individual commonly used values
export const API_BASE_URL = environmentConfig.get('API_BASE_URL');
export const WS_BASE_URL = environmentConfig.get('WS_BASE_URL');
export const WEB_BASE_URL = environmentConfig.get('WEB_BASE_URL');
export const SSL_PINS = environmentConfig.get('SSL_PINS', []);